import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { isNewVersionAvailable, updateChecksEnabled } from "../../services/appUpdate";

// "A new version is available" prompt. Checks for a new deploy every few minutes and whenever the
// tab/app comes back into focus; the user refreshes when it suits them ("Later" asks again later).

const CHECK_EVERY_MS = 5 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000; // focus/visibility checks are throttled to one a minute
const SNOOZE_MS = 30 * 60 * 1000;

function UpdatePrompt() {
  const [available, setAvailable] = useState(false);
  const [snoozedUntil, setSnoozedUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  const lastCheckRef = useRef(0);

  const check = useCallback(async (force = false) => {
    if (!force && Date.now() - lastCheckRef.current < MIN_GAP_MS) return;
    lastCheckRef.current = Date.now();
    try {
      if (await isNewVersionAvailable()) setAvailable(true);
    } catch {
      /* offline or the site is mid-deploy: try again next time */
    }
    setNow(Date.now());
  }, []);

  useEffect(() => {
    if (!updateChecksEnabled()) return undefined;
    const first = setTimeout(() => check(true), 15 * 1000);
    const timer = setInterval(() => check(true), CHECK_EVERY_MS);
    const onFocus = () => document.visibilityState === "visible" && check();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearTimeout(first);
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [check]);

  if (!available || now < snoozedUntil) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-update-prompt
      className="fixed bottom-4 left-4 right-4 sm:right-auto sm:w-[380px] z-[70] flex items-start gap-3 rounded-2xl border border-app-primary bg-app-card p-4 shadow-2xl"
    >
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-500/15">
        <RefreshCw className="h-5 w-5 text-[#2470B7] dark:text-blue-300" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-app-heading">A new version is available</p>
        <p className="mt-0.5 text-xs text-app-muted">Refresh to get the latest features and fixes. Finish anything you&apos;re typing first.</p>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-[#2470B7] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#1d5d99]"
          >
            Refresh now
          </button>
          <button
            type="button"
            onClick={() => setSnoozedUntil(Date.now() + SNOOZE_MS)}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-app-muted hover:bg-app-hover"
          >
            Later
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setSnoozedUntil(Date.now() + SNOOZE_MS)}
        className="rounded-md p-1 text-app-muted hover:bg-app-hover"
        aria-label="Remind me later"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default UpdatePrompt;
